import type { Control, FieldErrors, UseFormSetValue } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  FieldRequiredIndicator,
  FieldRoot,
  Input,
  SimpleGrid,
  Stack,
} from '@enechain/ecloud-designsystem'

import { FieldErrorText, FieldLabel } from '~/components/common/Field'
import { AccountSection } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/components/AccountSection'
import type { TradingPartnerFormInput } from '~/pages/AdminOcctoMaster/tabs/TradingPartner/models/schema'

type Props = {
  control: Control<TradingPartnerFormInput>
  errors: FieldErrors<TradingPartnerFormInput>
  setValue: UseFormSetValue<TradingPartnerFormInput>
}

/**
 * 取引先マスタの Create / Edit 共通 form fields コンポーネント。
 *
 * D1: tradingPartnerCode を削除 (partner 単位コードは制度上不在)。
 * D3: notificationRequired を削除 (契約属性で取引先の責務外)。
 * 残るのは name / shortName / accounts のみ。
 * accounts 編集 UI は AccountSection に委譲。
 */
export const TradingPartnerFormFields = ({
  control,
  errors,
  setValue,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <Stack gap={4}>
      {/* name + shortName: 2カラム (design: 取引先名称/略称 = 1fr 1fr) */}
      <SimpleGrid gap={3} columns={2}>
        {/* name */}
        <Controller
          control={control}
          name="name"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.name !== undefined}
              required
            >
              <FieldLabel htmlFor="trading-partner-name">
                {t('page:adminOcctoMaster.tradingPartner.form.name')}
                <FieldRequiredIndicator />
              </FieldLabel>
              <Input
                {...field}
                id="trading-partner-name"
                placeholder={t(
                  'page:adminOcctoMaster.tradingPartner.form.namePlaceholder',
                )}
              />
              {errors.name !== undefined && (
                <FieldErrorText>{errors.name.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />

        {/* shortName */}
        <Controller
          control={control}
          name="shortName"
          render={({ field }) => (
            <FieldRoot
              alignItems="stretch"
              invalid={errors.shortName !== undefined}
              required
            >
              <FieldLabel htmlFor="trading-partner-short-name">
                {t('page:adminOcctoMaster.tradingPartner.form.shortName')}
                <FieldRequiredIndicator />
              </FieldLabel>
              <Input {...field} id="trading-partner-short-name" />
              {errors.shortName !== undefined && (
                <FieldErrorText>{errors.shortName.message}</FieldErrorText>
              )}
            </FieldRoot>
          )}
        />
      </SimpleGrid>

      {/* accounts */}
      <AccountSection control={control} errors={errors} setValue={setValue} />
    </Stack>
  )
}
